import React, { useState, useCallback } from 'react';
import { fetchWithTimeout, TimeoutError, NetworkError, categorizeError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

/**
 * Test page for verifying timeout handling functionality
 * This page demonstrates:
 * 1. Normal API calls with timeout handling
 * 2. Forced timeout scenarios (using 1ms timeout)
 * 3. Error display and retry functionality
 */
const TimeoutTest = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastAction, setLastAction] = useState('');

  // Normal API call with reasonable timeout (15 seconds)
  const testNormalTimeout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setLastAction('Normal API Call (15s timeout)');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/auth/me`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        15000 // 15 second timeout - should succeed
      );

      if (response.ok) {
        const data = await response.json();
        setResult({ success: true, message: 'API call succeeded!', data });
      } else {
        setResult({ success: true, message: `API returned status ${response.status} (expected for unauthenticated request)` });
      }
    } catch (err) {
      console.log('Caught error:', err);
      setError(err);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  // Force timeout with 1ms timeout (will always timeout)
  const testForcedTimeout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setLastAction('Forced Timeout (1ms timeout)');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/auth/me`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        1 // 1ms timeout - will always timeout
      );

      const data = await response.json();
      setResult({ success: true, message: 'Unexpectedly succeeded!', data });
    } catch (err) {
      console.log('Expected timeout error:', err);
      setError(err);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  // Retry handler
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    // Retry with normal timeout
    testNormalTimeout();
  }, [testNormalTimeout]);

  // Test network error (invalid URL)
  const testNetworkError = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setLastAction('Network Error (invalid URL)');

    try {
      const response = await fetchWithTimeout(
        'http://invalid-server-that-does-not-exist.local/api/test',
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        5000
      );

      const data = await response.json();
      setResult({ success: true, message: 'Unexpectedly succeeded!', data });
    } catch (err) {
      console.log('Expected network error:', err);
      setError(err);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            API Timeout Test
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Test page for Feature #61: API timeout handled gracefully
          </p>
        </div>

        {/* Test Buttons */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Test Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={testNormalTimeout}
              disabled={isLoading}
              className="px-4 py-3 bg-green-500/20 border border-green-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-green-400 hover:bg-green-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && lastAction.includes('Normal') ? 'Loading...' : 'Normal API Call'}
            </button>
            <button
              onClick={testForcedTimeout}
              disabled={isLoading}
              className="px-4 py-3 bg-yellow-500/20 border border-yellow-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-yellow-400 hover:bg-yellow-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && lastAction.includes('Forced') ? 'Loading...' : 'Force Timeout'}
            </button>
            <button
              onClick={testNetworkError}
              disabled={isLoading}
              className="px-4 py-3 bg-orange-500/20 border border-orange-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-orange-400 hover:bg-orange-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && lastAction.includes('Network') ? 'Loading...' : 'Network Error'}
            </button>
          </div>
          <p className="font-body text-xs text-slate-400 dark:text-silver/40 mt-4">
            Click the buttons above to test different error scenarios. The "Force Timeout" button uses a 1ms timeout which will always fail.
          </p>
        </div>

        {/* Last Action */}
        {lastAction && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-4 mb-6">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40">
              Last Action: {lastAction}
            </p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <ApiErrorAlert
            error={error}
            onRetry={handleRetry}
            isRetrying={isRetrying}
          />
        )}

        {/* Success Result */}
        {result && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
            <p className="text-green-400 text-sm font-medium mb-2">
              Success
            </p>
            <p className="text-green-300/80 text-sm">
              {result.message}
            </p>
            {result.data && (
              <pre className="mt-3 text-xs text-slate-600 dark:text-silver/60 bg-black/10 dark:bg-black/20 p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
            <span className="ml-3 font-mono text-sm text-light-text-secondary dark:text-silver/60">Processing...</span>
          </div>
        )}

        {/* Technical Info */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Technical Details
          </h2>
          <div className="space-y-3 font-body text-sm text-slate-600 dark:text-silver/80">
            <p>
              <strong className="text-light-text dark:text-pure-white">Timeout Handling:</strong> The app uses <code className="bg-black/5 dark:bg-white/5 px-1 rounded">fetchWithTimeout()</code> which wraps the native fetch API with an AbortController.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">Default Timeout:</strong> 15 seconds for most API calls.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">Error Types:</strong>
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-xs">
              <li><span className="text-yellow-600 dark:text-yellow-400">TimeoutError</span> - Request took too long (yellow alert)</li>
              <li><span className="text-orange-600 dark:text-orange-400">NetworkError</span> - Connection failed (orange alert)</li>
              <li><span className="text-red-600 dark:text-red-400">Other Errors</span> - Unexpected errors (red alert)</li>
            </ul>
            <p>
              <strong className="text-light-text dark:text-pure-white">Recovery:</strong> All timeout and network errors show a retry button. The app remains fully functional after any error.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeoutTest;
